import { useEffect, useState } from 'react';

type DeviceType = 'ios' | 'android' | 'other';

interface PWAInstallPromptProps {
  onDismiss?: () => void;
}

const STORAGE_KEY = 'enpass_pwa_prompt_shown';

function detectDevice(): DeviceType {
  const ua = navigator.userAgent.toLowerCase();
  
  // iOS detection (iPhone, iPad, iPod)
  if (/iphone|ipad|ipod/.test(ua)) {
    return 'ios';
  }
  
  // Android detection
  if (/android/.test(ua)) {
    return 'android';
  }
  
  return 'other';
}

function isStandalone(): boolean {
  // Check if already running as PWA
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

function isMobile(): boolean {
  return /iphone|ipad|ipod|android|mobile/i.test(navigator.userAgent);
}

export function PWAInstallPrompt({ onDismiss }: PWAInstallPromptProps) {
  const [show, setShow] = useState(false);
  const [device, setDevice] = useState<DeviceType>('other');
  const [step, setStep] = useState(0);

  useEffect(() => {
    // Only show on mobile
    if (!isMobile()) return;
    
    // Don't show if already installed as PWA
    if (isStandalone()) return;
    
    // Check if already shown
    const shown = localStorage.getItem(STORAGE_KEY);
    if (shown) return;
    
    // Detect device and show prompt
    setDevice(detectDevice());
    
    // Delay show for better UX
    const timer = setTimeout(() => {
      setShow(true);
    }, 2000);
    
    return () => clearTimeout(timer);
  }, []);

  const handleDismiss = () => {
    localStorage.setItem(STORAGE_KEY, Date.now().toString());
    setShow(false);
    onDismiss?.();
  };

  const handleRemindLater = () => {
    // Don't set storage, will show again next visit
    setShow(false);
    onDismiss?.();
  };

  if (!show) return null;

  const iosSteps = [
    {
      title: '点击分享按钮',
      desc: '在 Safari 底部工具栏，点击中间的「分享」图标（方框+向上箭头）',
      icon: '⬆️',
    },
    {
      title: '选择「添加到主屏幕」',
      desc: '在弹出的菜单中向下滑动，找到并点击「添加到主屏幕」',
      icon: '➕',
    },
    {
      title: '确认添加',
      desc: '点击右上角的「添加」按钮，enPASS 图标将出现在主屏幕',
      icon: '✓',
    },
  ];

  const androidSteps = [
    {
      title: '点击菜单按钮',
      desc: '在浏览器右上角点击「⋮」三点菜单按钮',
      icon: '⋮',
    },
    {
      title: '选择「添加到主屏幕」',
      desc: '在菜单中找到「添加到主屏幕」或「安装应用」选项',
      icon: '➕',
    },
    {
      title: '确认安装',
      desc: '点击「安装」或「添加」，应用图标将出现在主屏幕',
      icon: '✓',
    },
  ];

  const genericSteps = [
    {
      title: '打开浏览器菜单',
      desc: '点击浏览器的菜单按钮（通常在右上角或底部）',
      icon: '☰',
    },
    {
      title: '添加到主屏幕',
      desc: '找到「添加到主屏幕」、「安装应用」或类似选项',
      icon: '➕',
    },
    {
      title: '完成安装',
      desc: '确认后，enPASS 将像原生应用一样出现在主屏幕',
      icon: '✓',
    },
  ];

  const steps = device === 'ios' ? iosSteps : device === 'android' ? androidSteps : genericSteps;
  const currentStep = steps[step];
  const isLastStep = step === steps.length - 1;

  return (
    <div className="pwa-overlay" onClick={handleRemindLater}>
      <div className="pwa-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="pwa-header">
          <div className="pwa-icon">
            <img src="/icon.svg" alt="enPASS" width="48" height="48" />
          </div>
          <div>
            <h2 className="pwa-title">安装 enPASS</h2>
            <p className="pwa-subtitle">添加到主屏幕，获得更好体验</p>
          </div>
        </div>

        {/* Benefits */}
        <div className="pwa-benefits">
          <span className="pwa-benefit">📴 离线可用</span>
          <span className="pwa-benefit">⚡ 秒开启动</span>
          <span className="pwa-benefit">🔔 全屏体验</span>
        </div>

        {/* Device indicator */}
        <div className="pwa-device">
          {device === 'ios' && '📱 检测到 iPhone/iPad'}
          {device === 'android' && '📱 检测到 Android'}
          {device === 'other' && '📱 移动设备'}
        </div>

        {/* Step by step guide */}
        <div className="pwa-steps">
          <div className="pwa-step-header">
            <span className="pwa-step-number">步骤 {step + 1}/{steps.length}</span>
          </div>
          
          <div className="pwa-step-content">
            <div className="pwa-step-icon">{currentStep.icon}</div>
            <div className="pwa-step-text">
              <h3>{currentStep.title}</h3>
              <p>{currentStep.desc}</p>
            </div>
          </div>

          {/* Progress dots */}
          <div className="pwa-dots">
            {steps.map((_, i) => (
              <span 
                key={i} 
                className={`pwa-dot ${i === step ? 'active' : ''} ${i < step ? 'done' : ''}`}
                onClick={() => setStep(i)}
              />
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="pwa-actions">
          {!isLastStep ? (
            <>
              <button className="pwa-btn secondary" onClick={handleRemindLater}>
                稍后再说
              </button>
              <button className="pwa-btn primary" onClick={() => setStep(step + 1)}>
                下一步
              </button>
            </>
          ) : (
            <>
              <button className="pwa-btn secondary" onClick={() => setStep(0)}>
                重看教程
              </button>
              <button className="pwa-btn primary" onClick={handleDismiss}>
                我知道了
              </button>
            </>
          )}
        </div>

        {/* Skip link */}
        <button className="pwa-skip" onClick={handleDismiss}>
          不再提示
        </button>
      </div>
    </div>
  );
}

export default PWAInstallPrompt;
